import type { ORGANIZATION } from "@shared-kernel/domain/organization";
import type { TeacherLessonListVM } from "@shared-kernel/application/ports/teacher/teacher-repository";
import type {
  AdminDocumentaryResourceVM,
  CreateDocumentaryResourceBody,
  UpdateDocumentaryResourceBody,
} from "@shared-kernel/domain/types/documentary-resource";
import type { ApiTrainingSchedule } from "@shared-kernel/core/types/api-training-schedule";
import type { Speaker, SubcontractingContract } from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import type {
  EXTERNAL_PROVIDER_BATCH_REQUEST_STATUS,
  EXTERNAL_PROVIDER_BATCH_PARTICIPATION_STATUS,
  EXTERNAL_PROVIDER_BATCH_PARTICIPATION_PAYMENT_STATUS,
  EXTERNAL_PROVIDER_BATCH_PARTICIPATION_PAYMENT_FAILURE_REASON,
  EXTERNAL_PROVIDER_BATCH_ADMIN_MERGED_STATUS,
} from "@academy-context/shared/domain/types/enums/external-provider-batch-request";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import type { BatchEnrollmentCandidateVM } from "@shared-kernel/application/ports/admin/batch-enrollment-candidate";
import type { ATTACHMENT_LABEL } from "@academy-context/shared/external-provider-batch/types/provider-training-form.types";
import type { AdministrativePostalAddress, TrainingLocation } from "@shared-kernel/domain/types/postal-address";
import type { SUPPORTED_COUNTRY } from "@shared-kernel/domain/types/supported-country";
import type { AddressLookup } from "@shared-kernel/domain/types/address-lookup";
import type { StudioEnvironment } from "@academy-context/shared/domain/types/studio-environment";
import type { DateInterval } from "@shared-kernel/core/types/date-interval";
import type { BATCH_ORGANIZER_TYPE } from "@academy-context/shared/domain/types/enums/batch-organizer-type";
import type { BATCH_STATUS } from "@academy-context/shared/domain/types/enums/batch-status";
import type {
  BatchStudio,
  BatchDetailTrainingSchedule,
  BatchDetailAdminTeamMember,
  BatchDetailTeachingTeamMember,
  BatchQuestion,
} from "@academy-context/read/domain/types/shared/batch-item";
import type { BATCH_ENROLLMENT_STATUS } from "@academy-context/read/domain/types/enums/batch-enrollment-status";
import type { BATCH_ENROLLMENT_INVITATION_STATUS } from "@academy-context/read/domain/types/enums/batch-enrollment-invitation-status";
import type { BATCH_PARTICIPATION_MERGED_STATUS } from "@academy-context/read/domain/types/enums/batch-participation-merged-status";
import type { BatchEnrollmentSource } from "@academy-context/read/domain/types/shared/batch-enrollment-source";
import type { FundingRequestEligibilityVM, StudentHistoryEntryDTO } from "@shared-kernel/application/ports/student/student-repository";
import type { PaginatedResult } from "@shared-kernel/domain/types/paginated-result";
import type { FUNDING_REQUEST_STATUS } from "@academy-context/read/domain/types/enums/funding-request-status";
import type { FUNDING_REQUEST_ERROR_REASONS } from "@academy-context/read/domain/types/admin/funding-request";
import type {
  TRAINING_ITEM_TYPE,
  OneOnOneLessonsItem,
  ProviderBatchItem,
  TeacherBatchItem,
  ExternalProviderBatchItem,
  ReviewedOneOnOneLessonsItem,
  ReviewedExternalProviderBatchItem,
  ReviewedTrainingItem,
  Question,
} from "@academy-context/shared/domain/types/training-items";
import type { EligibleTrainingItemsVM } from "@academy-context/shared/domain/types/eligible-training-items";
import type { PaginationParams } from "@shared-kernel/domain/types/pagination-params";
import type { USER_STATUS, BALANCE_DELTA_PERIOD } from "@user-management-context/read/domain/types/admin/student";
import type { TEACHER_PAYOUT_MODE, TEACHER_STANDING, STRUCTURE_TYPE } from "@user-management-context/read/domain/types/admin/teacher";
import type { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import type { UserDetailsRoleVM } from "@user-management-context/shared/domain/types/user-details-role-vm";
import type { OnboardingStatus } from "@user-management-context/shared/domain/types/enums/onboarding-status";
import type { ISSUE_TYPE } from "@user-management-context/write/domain/types/shared/issue-reporting";
import type {
  ACTION_URGENCY_LEVEL,
  TRAINING_FOLLOW_UP_STATUS,
  TRAINING_PACE_STATUS,
  OBJECTIVE_ACQUISITION_STATUS,
} from "@academy-context/read/domain/types/training-follow-up";
import type { EDUCATIONAL_ADVISOR } from "@academy-context/shared/domain/types/enums/education-advisors";
import type { SURVEY_QUESTION_TYPE } from "@academy-context/shared/domain/types/enums/survey";
import type { FUNDER, FUNDER_TYPE } from "@academy-context/shared/domain/types/enums/funders";
import type { QUOTE_ISSUANCE_TRIGGER } from "@academy-context/shared/domain/types/enums/quote-issuance-trigger";
import type { LOCATION_TYPE } from "@academy-context/write/domain/services/locations";
import type { PaymentStatus } from "@academy-context/read/domain/types/enums/payment-status";
import type { TRAINING_LEVEL } from "@academy-context/shared/domain/types/enums/training-levels";
import type { TrainingDaysConfig } from "@academy-context/read/domain/types/shared/training-day";
import type { LeafId, MAIN_SPECIALIZATIONS } from "@academy-context/shared/domain/types/enums/specializations";
import type { Certification } from "@academy-context/shared/domain/types/enums/certification";
import type { TeachingMethodology } from "@academy-context/shared/domain/types/enums/teaching-methodology";
import type { TEACHER_PAYOUT_STATUS } from "@invoicing-context/read/domain/types/teacher/payout";

// ========================================
// User Management Entity Types
// ========================================

export type ImpersonateResponseVM = {
  accessToken: string;
  expiresIn: number;
};

export interface UserVM {
  id: string;
  email: string;
  roles: UserDetailsRoleVM[];
  name: string;
  lastName: string;
  displayName: string;
  isAdmin: boolean;
  invitationStatus: USER_STATUS;
}

export interface ImpersonationCandidateVM {
  id: string;
  email: string;
  displayName: string;
}

export interface UserDetailVM {
  id: string;
  email: string;
  name: string;
  lastName: string;
  mobile: Nullable<string>;
  profilePictureUrl: Nullable<string>;
  isAdmin: boolean;
  roles: UserDetailRoleVM[];
  invitationStatus: USER_STATUS;
}

export type UserDetailRoleVM =
  | { role: ROLES.STUDENT; status: OnboardingStatus; adminSettings: StudentAdminSettings }
  | { role: ROLES.TEACHER; status: OnboardingStatus; adminSettings: TeacherAdminSettings }
  | { role: ROLES.PROVIDER; status: OnboardingStatus; adminSettings: ProviderAdminSettings };

export interface AdminVM {
  id: string;
  email: string;
  name: string;
  lastName: string;
}

export interface AdminStudentVM extends StudentAdminSettings {
  id: string;
  userId: string;
  name: string;
  lastName: string;
  email: string;
  mobile: Nullable<string>;
  profilePictureUrl: string | null;
  stageName: Nullable<string>;
}

export interface StudentListElementVM {
  userId: string;
  name: string;
  lastName: string;
  email: string;
  details: {
    studentId: string;
    balance: {
      global: number;
      byOrganization: Record<ORGANIZATION, number>;
      deltas: {
        [BALANCE_DELTA_PERIOD.Last10Days]: number;
        [BALANCE_DELTA_PERIOD.Last30Days]: number;
        [BALANCE_DELTA_PERIOD.Last90Days]: number;
        [BALANCE_DELTA_PERIOD.Last365Days]: number;
      };
    };
    canAddLessons: boolean;
    afdasISWaitingPeriodEndDate: Nullable<string>;
    lastFranceTravailTrainingEndDate: Nullable<string>;
    isFundingRequestInProgress: boolean;
    admin: {
      id: string;
      name: string;
      lastName: string;
    } | null;
  };
}

export interface AdminTeacherVM {
  id: string;
  userId: string;
  slug: string;
  /** Ready-to-use public profile URL, built by the backend; null when the profile is not publicly referenced. */
  publicProfileUrl: Nullable<string>;
  name: string;
  lastName: string;
  email: string;
  mobile: Nullable<string>;
  profilePictureUrl: Nullable<string>;
  defaultPrice: Nullable<number>;
  siret: Nullable<string>;
  iban: Nullable<string>;
  adminNote: Nullable<string>;
  activityRegistrationNumber: Nullable<string>;
  subcontractingContractUrl: Nullable<string>;
  organization: ORGANIZATION;
  payoutMode: TEACHER_PAYOUT_MODE;
  structureType: STRUCTURE_TYPE;
  businessName: Nullable<string>;
  standing: TEACHER_STANDING;
  postalAddress: Nullable<AdministrativePostalAddress>;
  favoriteStudio: Nullable<{
    id: string;
  }>;
  websiteUrl: Nullable<string>;
  instagramUrl: Nullable<string>;
  youtubeUrl: Nullable<string>;
  tiktokUrl: Nullable<string>;
  facebookUrl: Nullable<string>;
  isPublicProfileVisibleByTeacher: boolean;
  isPublicProfileVisibleByAdmin: boolean;
  isEmailVisibleOnPublicProfile: boolean;
  artistResumeUrl: Nullable<string>;
  teacherResumeUrl: Nullable<string>;
  description: Nullable<string>;
  pedagogicalExperience: Nullable<string>;
  specializations: Nullable<LeafId[]>;
  certifications: Nullable<Certification[]>;
  teachingMethodologies: Nullable<TeachingMethodology[]>;
  publicTeachingVenues: {
    studios: { id: string; name: string }[];
    offersOnlineCoaching: boolean;
  };
}

export interface TeacherListElementVM {
  teacherId: string;
  name: string;
  lastName: string;
  email: string;
  defaultPrice: Nullable<number>;
  adminNote: Nullable<string>;
  siret: Nullable<string>;
  activityRegistrationNumber: Nullable<string>;
  yearlyEarnings: number;
  organization: ORGANIZATION;
  payoutMode: TEACHER_PAYOUT_MODE;
  oldestNonIncludedInPayoutLessonDate: Nullable<string>;
  outstandingPayoutAmount: number;
  standing: TEACHER_STANDING;
  favoriteStudioPostalAddress: Nullable<TrainingLocation>;
}

export interface AdminProviderVM {
  id: string;
  userId: string;
  name: string;
  tag: string;
  email: string;
  mobile: Nullable<string>;
  profilePictureUrl: Nullable<string>;
  iban: Nullable<string>;
  siret: string;
  activityRegistrationNumber: Nullable<string>;
  legalRepresentativeFirstName: string;
  legalRepresentativeLastName: string;
  accountFirstName: string;
  accountLastName: string;
  postalAddress: AdministrativePostalAddress;
}

export interface ProviderListItemVM {
  id: string;
  name: string;
  postalAddress: AdministrativePostalAddress;
  tag: string;
  batches: {
    id: string;
    title: string;
    interval: DateInterval;
    questions: { text: string; order: number }[];
  }[];
}

export type UserInvitationVM = {
  userId: string;
  email: string;
  roles: ROLES[];
  isAdmin: boolean;
  updatedAt: string;
  isExpired: boolean;
  name: string;
  lastName: string;
};

export type UninvitedUserVM = {
  userId: string;
  email: string;
  name: string;
  lastName: string;
  roles: ROLES[];
  isAdmin: boolean;
  createdAt: string;
};

// ========================================
// User Management Write Types
// ========================================

export interface UpdateUserAccountBody {
  name: string;
  lastName: string;
  email: string;
  mobile: Nullable<string>;
  profilePictureUrl: Nullable<string>;
}

export type UpdateStudentSettingsBody = StudentAdminSettings;

export interface UpdateTeacherSettingsBody {
  standing: TEACHER_STANDING;
  adminNote: Nullable<string>;
  payoutMode: TEACHER_PAYOUT_MODE;
  organization: ORGANIZATION;
  subcontractingContractUrl: Nullable<string>;
  isPublicProfileVisibleByAdmin: boolean;
}

export interface UpdateProviderSettingsBody {
  tag: string;
}

export type StudentAdminSettings = {
  favoriteTeachers: string[];
  areLessonsEnabled: boolean;
  overdraftLimit: number;
  canRequestARefundOnce: boolean;
  canRequestANonAfdasISFundingOnce: boolean;
  canBypassFundingRequestBalanceCeilingOnce: boolean;
  adminId: Nullable<string>;
};

export type TeacherAdminSettings = {
  standing: TEACHER_STANDING;
  adminNote: Nullable<string>;
  payoutMode: TEACHER_PAYOUT_MODE;
  organization: ORGANIZATION;
  subcontractingContractUrl: Nullable<string>;
  isPublicProfileVisibleByAdmin: boolean;
};

export type ProviderAdminSettings = {
  tag: string;
};

export type RoleInvitationInput =
  | { role: ROLES.TEACHER; adminSettings: TeacherAdminSettings }
  | { role: ROLES.STUDENT; adminSettings: StudentAdminSettings }
  | { role: ROLES.PROVIDER; adminSettings: ProviderAdminSettings };

export type RoleInput = RoleInvitationInput;

interface UserAccountFields {
  email: string;
  name: string;
  lastName: string;
  mobile: Nullable<string>;
  profilePictureUrl: Nullable<string>;
  note: Nullable<string>;
}

export type CreateUserBody = (UserAccountFields & { isAdmin: false; roles: RoleInput[] }) | (UserAccountFields & { isAdmin: true });

export type UpdateRolesBody = { roles: RoleInput[] };

// ========================================
// Student Summary Types (duplicated from student-repository.ts)
// ========================================

export interface StudentSummaryVM {
  balance: number;
  afdasISWaitingPeriodEndDate: Nullable<string>;
  lastEntries: StudentHistoryEntryDTO[];
}

// ========================================
// Funding Request View Model Types (moved from @academy-context/read/domain/types/admin/funding-request)
// ========================================

type FundingRequestStudent = {
  id: string;
  name: string;
  phone: Nullable<string>;
};

type FundingRequestTraining = {
  startDate: string;
  endDate: string;
  price: number;
  operaOffCommission: number;
  hours: number;
};

type FundingRequestTeacher = {
  id: string;
  name: string;
  lastName: string;
};

type FundingRequestTeacherWithAddress = FundingRequestTeacher & {
  postalAddress: Nullable<TrainingLocation>;
};

type FundingRequestProvider = {
  id: string;
  name: string;
};

type FundingRequestBatch = {
  id: string;
  title: string;
};

export type ExtendedOneOnOneLessonsItem = Omit<OneOnOneLessonsItem, "teachers"> & {
  teachers: { teacher: FundingRequestTeacherWithAddress; monthlyLessonsCount: number }[];
};

export type ExtendedProviderBatchItem = Omit<ProviderBatchItem, "providerId" | "batchId"> & {
  provider: FundingRequestProvider;
  batch: FundingRequestBatch;
};

export type ExtendedTeacherBatchItem = Omit<TeacherBatchItem, "teacherId" | "batchId"> & {
  teacher: FundingRequestTeacher;
  batch: FundingRequestBatch;
};

export type ExtendedExternalProviderBatchItem = Omit<ExternalProviderBatchItem, "externalProviderBatchRequestId"> & {
  externalProviderBatchRequest: { id: string; title: string; providerName: string };
};

export type ExtendedTrainingItem =
  | ExtendedOneOnOneLessonsItem
  | ExtendedProviderBatchItem
  | ExtendedTeacherBatchItem
  | ExtendedExternalProviderBatchItem;

export type ExtendedReviewedOneOnOneLessonsItem = Omit<ReviewedOneOnOneLessonsItem, "teachers"> & {
  teachers: { teacher: FundingRequestTeacherWithAddress }[];
};

export type ExtendedReviewedProviderBatchItem = ExtendedProviderBatchItem;

export type ExtendedReviewedTeacherBatchItem = ExtendedTeacherBatchItem;

export type ExtendedReviewedExternalProviderBatchItem = Omit<ReviewedExternalProviderBatchItem, "externalProviderBatchRequestId"> & {
  externalProviderBatchRequest: {
    id: string;
    title: string;
    providerName: string;
  };
};

export type ExtendedReviewedTrainingItem =
  | ExtendedReviewedOneOnOneLessonsItem
  | ExtendedReviewedProviderBatchItem
  | ExtendedReviewedTeacherBatchItem
  | ExtendedReviewedExternalProviderBatchItem;

export type ApprovedExtendedReviewedOneOnOneLessonsItem = ExtendedReviewedOneOnOneLessonsItem & { questions: Question[] };
export type ApprovedExtendedReviewedProviderBatchItem = ExtendedReviewedProviderBatchItem & { questions: Question[] };
export type ApprovedExtendedReviewedTeacherBatchItem = ExtendedReviewedTeacherBatchItem & { questions: Question[] };

export type ApprovedExtendedReviewedTrainingItem =
  | ApprovedExtendedReviewedOneOnOneLessonsItem
  | ApprovedExtendedReviewedProviderBatchItem
  | ApprovedExtendedReviewedTeacherBatchItem
  | ExtendedReviewedExternalProviderBatchItem;

interface FundingRequestBase {
  id: string;
  student: FundingRequestStudent;
  funder: FUNDER_TYPE;
  error: Nullable<FUNDING_REQUEST_ERROR_REASONS>;
  hasAlreadyBeenReviewed: boolean;
  isReviewDraft: boolean;
  adminNote: Nullable<string>;
  afdasISWaitingPeriodEndDate: Nullable<string>;
  urgent: boolean;
  wantsAfdasExpenses: boolean;
}

export type ToReviewFundingRequestVM = FundingRequestBase & {
  status: "to_review";
  creationDate: string;
  reviewDate: Nullable<string>;
  submissionDate: Nullable<string>;
  organization: Nullable<ORGANIZATION>;
  batches: { startDate: string; type: TRAINING_ITEM_TYPE }[];
};

export type SubmittedToFunderFundingRequestVM = FundingRequestBase & {
  status: "submitted_to_funder";
  creationDate: string;
  reviewDate: string;
  submissionDate: string;
  training: FundingRequestTraining;
  organization: ORGANIZATION;
  reviewedTrainingItems: ExtendedReviewedTrainingItem[];
};

export type ReviewedFundingRequestVM = FundingRequestBase & {
  id: number;
  status: "reviewed";
  creationDate: string;
  reviewDate: string;
  submissionDate: string;
  training: FundingRequestTraining;
  organization: ORGANIZATION;
};

export type ApprovedFundingRequestVM = FundingRequestBase & {
  id: number;
  status: "approved";
  creationDate: string;
  reviewDate: string;
  submissionDate: string;
  obtentionDate: string;
  organization: ORGANIZATION;
  training: {
    startDate: string;
    endDate: string;
    price: number;
    operaOffCommission: number;
    hours: number;
  };
  externalFundingRequestId: string;
};

export type FundingRequestListItemVM = (
  | ToReviewFundingRequestVM
  | SubmittedToFunderFundingRequestVM
  | ReviewedFundingRequestVM
  | ApprovedFundingRequestVM
)[];

export interface ApprovedFundingRequestDetailVM {
  id: string;
  studentId: string;
  student: { name: string; lastName: string };
  resumeOrBioUrl: Nullable<string>;
  status: FUNDING_REQUEST_STATUS;
  creationDate: string;
  trainingName: string;
  trainingPrice: number;
  trainingDaysConfig: TrainingDaysConfig;
  reviewDate: string;
  submissionDate: string;
  issuedQuotes: IssuedQuoteVM[];
  educationalProjectUrl: Nullable<string>;
  coverLetterUrl: Nullable<string>;
  funder: FUNDER_TYPE;
  reviewedTrainingItems: ApprovedExtendedReviewedTrainingItem[];
  reviewedSpecializations: MAIN_SPECIALIZATIONS[];
  reviewedOtherSpecialization: Nullable<string>;
  reviewedSpecificAccommodation: Nullable<string>;
  level: Nullable<TRAINING_LEVEL>;
  reviewedAfdasTravelExpenses: Nullable<number>;
  reviewedAfdasAccommodationExpenses: Nullable<number>;
  reviewedAfdasHasPersonalVehicle: boolean;
  reviewedAfdasTrainingDates: Nullable<{ date: string }[]>;
  adminNote: Nullable<string>;
  afdasISWaitingPeriodInMonths: Nullable<number>;
  externalFundingRequestId: Nullable<string>;
  obtentionDate: string;
  studentNote: Nullable<string>;
}

export interface FundingRequestVM {
  id: string;
  urgent: boolean;
  student: { fullName: string; id: string; userId: string };
  trainingItems: ExtendedTrainingItem[];
  reviewedTrainingItems: Nullable<ExtendedReviewedTrainingItem[]>;
  funder: FUNDER_TYPE;
  // What the student submitted, kept as history next to the value the admin validated.
  specializations: MAIN_SPECIALIZATIONS[];
  otherSpecialization: Nullable<string>;
  // What the admin validated, null until they rule — the review form then starts from the student's pick.
  reviewedSpecializations: Nullable<MAIN_SPECIALIZATIONS[]>;
  reviewedOtherSpecialization: Nullable<string>;
  resumeOrBioUrl: string;
  coverLetter: string;
  personalizedEducationalProject: string;
  trainingPrice: Nullable<number>;
  trainingDaysConfig: Nullable<TrainingDaysConfig>;
  reviewedCoverLetter: Nullable<string>;
  reviewedPersonalizedEducationalProject: Nullable<string>;
  status: FUNDING_REQUEST_STATUS;
  specificAccommodation: Nullable<string>;
  reviewedSpecificAccommodation: Nullable<string>;
  level: Nullable<TRAINING_LEVEL>;
  afdasTravelExpenses: Nullable<number>;
  reviewedAfdasTravelExpenses: Nullable<number>;
  afdasAccommodationExpenses: Nullable<number>;
  reviewedAfdasAccommodationExpenses: Nullable<number>;
  afdasHasPersonalVehicle: boolean;
  reviewedAfdasHasPersonalVehicle: boolean;
  afdasTrainingDates: Nullable<{ date: string }[]>;
  reviewedAfdasTrainingDates: Nullable<{ date: string }[]>;
  operaOffCommission: Nullable<number>;
  adminNote: Nullable<string>;
  organization: Nullable<ORGANIZATION>;
  isReviewDraft: boolean;
  hasAlreadyBeenReviewed: boolean;
  studentNote: Nullable<string>;
}

// ========================================
// Funding Request Body & Pagination Types (moved from shared-repository.ts)
// ========================================

export interface ReviewFundingRequestBody {
  urgent: boolean;
  reviewedTrainingItems: ReviewedTrainingItem[];
  reviewedSpecializations: MAIN_SPECIALIZATIONS[];
  reviewedOtherSpecialization: Nullable<string>;
  reviewedCoverLetter: string;
  reviewedPersonalizedEducationalProject: string;
  trainingPrice: number;
  operaOffCommission: number;
  trainingDaysConfig: TrainingDaysConfig;
  level: TRAINING_LEVEL;
  reviewedSpecificAccommodation: Nullable<string>;
  adminNote: Nullable<string>;
  organization: ORGANIZATION;
  reviewedAfdasTravelExpenses: number | null;
  reviewedAfdasAccommodationExpenses: number | null;
  reviewedAfdasHasPersonalVehicle: boolean;
  reviewedAfdasTrainingDates: { date: string }[] | null;
}

export interface UpdateFundingRequestBody {
  id: string;
  urgent: boolean;
  reviewedTrainingItems: Nullable<ReviewedTrainingItem[]>;
  // Nullable in the contract: the back accepts a draft on which the admin has not ruled yet. The review form always
  // sends the pair, seeded from the student's pick — which the admin then leaves as is or corrects.
  reviewedSpecializations: Nullable<MAIN_SPECIALIZATIONS[]>;
  reviewedOtherSpecialization: Nullable<string>;
  reviewedCoverLetter: Nullable<string>;
  reviewedPersonalizedEducationalProject: Nullable<string>;
  trainingPrice: Nullable<number>;
  operaOffCommission: Nullable<number>;
  trainingDaysConfig: Nullable<TrainingDaysConfig>;
  level: Nullable<TRAINING_LEVEL>;
  reviewedSpecificAccommodation: Nullable<string>;
  adminNote: Nullable<string>;
  organization: Nullable<ORGANIZATION>;
  reviewedAfdasTravelExpenses: number | null;
  reviewedAfdasAccommodationExpenses: number | null;
  reviewedAfdasHasPersonalVehicle: boolean;
  reviewedAfdasTrainingDates: { date: string }[] | null;
}

export interface ApproveFundingRequestBody {
  obtentionDate: string;
  externalFundingRequestId: string;
  fundingRequestId: string;
  operaOffCommission: number;
}

export type FundingSortField =
  | "obtentionDate"
  | "trainingStartDate"
  | "trainingEndDate"
  | "studentName"
  | "funder"
  | "price"
  | "operaOffCommission"
  | "organization";

export type FundingListPaginationParams = PaginationParams<FundingSortField>;

export interface IssuedQuoteVM {
  issueDate: string;
  trigger: QUOTE_ISSUANCE_TRIGGER;
  interval: DateInterval;
  hours: number;
  price: number;
  funder: FUNDER;
  quoteUrl: string;
}

export interface FundingListItemVM {
  id: string;
  status: FUNDING_REQUEST_STATUS;
  funder: FUNDER_TYPE;
  obtentionDate: string;
  student: {
    id: string;
    name: string;
    phone: Nullable<string>;
  };
  training: {
    startDate: string;
    endDate: string;
    price: number;
    operaOffCommission: number;
    hours: number;
    title: string;
  };
  organization: ORGANIZATION;
  externalFundingRequestId: Nullable<string>;
  urgent: boolean;
  latestIssuedQuote: Nullable<IssuedQuoteVM>;
}

// ========================================
// Funding Request Eligibility Types (duplicated from student-repository.ts)
// ========================================

// ========================================
// Payout Types
// ========================================

export interface AdminTeacherPayoutVM {
  id: string;
  teacher: {
    id: string;
    name: string;
    lastName: string;
  };
  lessons: {
    count: number;
    totalHours: number;
  };
  payout: {
    interval: {
      start: string;
      end: string;
    };
    creationDate: string;
    amount: number;
    status: TEACHER_PAYOUT_STATUS;
    invoice: {
      number: string;
      url: string;
      date: string;
      externalUrl: string | null;
    };
    organization: ORGANIZATION;
  };
}

export interface ValidateTeacherPayoutBody {
  payoutId: string;
}

export type TeacherPayoutSortField = "creationDate" | "amount" | "status";
export type TeacherPayoutFilterableStatus = "pending" | "approved" | "failed_to_send" | "sent_to_payment_provider";
export interface TeacherPayoutListPaginationParams extends Omit<PaginationParams<TeacherPayoutSortField>, "filters"> {
  filters?: {
    searchTerm?: string;
    statuses?: TeacherPayoutFilterableStatus[];
  };
}

// ========================================
// Teacher Lesson Types (duplicated from teacher-repository.ts)
// ========================================

export type LessonLocationVM =
  | {
      type: LOCATION_TYPE.ONLINE;
      studio: null;
      price: number;
      isPaidByTeacher: boolean;
    }
  | {
      type: LOCATION_TYPE.OTHER;
      studio: null;
      price: number;
      isPaidByTeacher: boolean;
    }
  | {
      type: LOCATION_TYPE.STUDIO;
      studio: {
        id: string;
        name: string;
      };
      price: number;
      isPaidByTeacher: boolean;
    };

// L'admin lit les séances d'un prof par le même handler que le prof lui-même : même contrat, une
// seule définition (importée en tête de fichier).
export type { TeacherLessonListVM };

// ========================================
// Payment Types (moved from @academy-context)
// ========================================

export interface StandardPayment {
  id: string;
  from: string;
  to: string;
  teacher: { id: string; name: string; lastName: string; siret: Nullable<string>; activityRegistrationNumber: Nullable<string> };
  amount: { global: number; byOrganization: Record<ORGANIZATION, number> };
  amountPaid: Nullable<number>;
  status: PaymentStatus;
  reference: Nullable<string>;
  paymentDate: Nullable<string>;
  organization: Nullable<ORGANIZATION>;
}

export interface ConventionPaymentVM {
  id: string;
  creationDate: string;
  teacher: string;
  student: string;
  amount: { global: number };
  amountPaid: Nullable<number>;
  status: PaymentStatus;
  reference: Nullable<string>;
  paymentDate: Nullable<string>;
}

export type ConventionPaymentExtended = ConventionPaymentVM & { paymentDateLocale: Nullable<string>; creationDateLocale: string };

export interface LessonVM {
  id: string;
  teacher: string;
  student: string;
  creationDate: string;
  date: string;
  hours: number;
  hourlyPrice: Nullable<number>;
  location: LessonLocationVM;
  paidToTeacher: number;
}

export type LessonListVM = LessonVM[];

export interface UpdatePaymentBody {
  type: "convention" | "standard";
  status: PaymentStatus;
  reference: Nullable<string>;
  paymentDate: Nullable<string>;
  amountPaid: Nullable<number>;
  organization: Nullable<ORGANIZATION>;
}

// ========================================
// Batch List Types
// ========================================

export interface AdminBatchListItem {
  id: string;
  title: string;
  price: number;
  totalHours: number;
  hourlyPrice: number;
  interval: DateInterval;
  creationDate: string;
  illustrationImageUrl: Nullable<string>;
  status: BATCH_STATUS;
  organizer: {
    id: string;
    userName: string;
    type: BATCH_ORGANIZER_TYPE;
  };
  enrolledCount: number;
  participantCount: number;
  questions: BatchQuestion[];
  postalAddress: TrainingLocation;
  isLegacy: boolean;
  teachingTeam: {
    firstName: string;
    lastName: string;
  }[];
}

// ========================================
// Batch Detail Types (moved from admin-batch-detail.ts)
// ========================================

interface AdminBatchItemStudent {
  id: string;
  profilePictureUrl: Nullable<string>;
  name: string;
  lastName: string;
}

export interface AdminBatchItemEnrollment {
  id: string;
  student: AdminBatchItemStudent;
  price: number;
  status: BATCH_ENROLLMENT_STATUS;
  creationDate: string;
  cancellationMessage: Nullable<string>;
  source: BatchEnrollmentSource;
  hasSufficientBalance: boolean;
  fundingRequestStatus: Nullable<FUNDING_REQUEST_STATUS>;
  fundingRequestStatusDate: Nullable<string>;
  lastUpdatedStatusDate: string;
  contractStatus: Nullable<string>;
  contractSignedAt: Nullable<string>;
  balanceBypassEnabled: boolean;
}

export interface AdminBatchItemParticipation {
  id: string;
  student: AdminBatchItemStudent;
  price: number;
  mergedStatus: BATCH_PARTICIPATION_MERGED_STATUS;
  creationDate: string;
  cancellationMessage: Nullable<string>;
  invoiceRefusalReason: Nullable<string>;
}

export interface AdminBatchItemInvitation {
  id: string;
  user: {
    userId: Nullable<string>;
    profilePictureUrl: Nullable<string>;
    name: string;
    lastName: string;
  };
  price: number;
  creationDate: string;
  // Entry into the current status. Never creationDate: "invitation sent 5 days ago" must not mean
  // "submitted 5 days ago" — the candidature may have waited months for our validation.
  statusSinceDate: string;
  status: BATCH_ENROLLMENT_INVITATION_STATUS;
  rejectionReason: Nullable<string>;
}

export interface AdminBatchDetailVM {
  id: string;
  slug: string;
  /** Ready-to-use public masterclass URL, built by the backend; null when not publicly visible. */
  publicBatchUrl: Nullable<string>;
  title: string;
  description: string;
  objective: string;
  price: number;
  questions: BatchQuestion[];
  interval: DateInterval;
  creationDate: string;
  enrollments: AdminBatchItemEnrollment[];
  participations: AdminBatchItemParticipation[];
  invitations: AdminBatchItemInvitation[];
  maxStudentCapacity: number;
  studio: BatchStudio;
  targetAudience: string;
  prerequisites: string;
  evaluationMethod: string;
  illustrationImageUrl: Nullable<string>;
  brochureUrl: Nullable<string>;
  websiteUrl: Nullable<string>;
  organizer: {
    id: string;
    userName: string;
    type: BATCH_ORGANIZER_TYPE;
  };
  trainingSchedules: BatchDetailTrainingSchedule[];
  adminTeamMembers: BatchDetailAdminTeamMember[];
  teachingTeamMembers: BatchDetailTeachingTeamMember[];
  isPublicVisible: boolean;
}

// ========================================
// Batch Payment Types (moved from batch-payment.ts)
// ========================================

export enum BATCH_PAYMENT_STATUS {
  INVOICE_TO_VALIDATE = "invoice_to_validate",
  INVOICE_REFUSED = "invoice_refused",
  TO_PROCESS_ON_PL = "to_process_on_pl",
  PL_ERROR = "pl_error",
  PAYMENT_PROCESSING = "payment_processing",
  PAID = "paid",
}

export interface BatchPaymentVM {
  id: string;
  status: BATCH_PAYMENT_STATUS;
  invoice: { url: string; number: string; date: string; externalUrl: Nullable<string> };
  creationDate: string;
  adminNote: Nullable<string>;
  student: {
    name: string;
    lastName: string;
  };
  organizer: {
    name: string;
    iban: string;
  };
  training: {
    title: string;
  };
  batch: {
    interval: DateInterval;
  };
  payment: {
    amount: number;
    amountPaid: Nullable<number>;
    date: Nullable<string>;
    reference: Nullable<string>;
    receivingOrganization: ORGANIZATION;
    payingOrganization: ORGANIZATION;
  };
}

// ========================================
// Studio Types
// ========================================

export interface AdminStudioListItem {
  id: string;
  name: string;
  postalAddress: TrainingLocation;
  equipmentDescription: string;
  photoUrls: string[];
  price: number;
  isMobilityAccessible: boolean;
  environment: StudioEnvironment;
}

export interface CreateAdminStudioBody {
  name: string;
  postalAddress: AddressLookup;
  addressComplement: Nullable<string>;
  equipmentDescription: string;
  photoUrls: string[];
  price: number;
  isMobilityAccessible: boolean;
  environment: StudioEnvironment;
}

export type UpdateAdminStudioBody = CreateAdminStudioBody & { id: string };

// ========================================
// Pending Actions Types (Read)
// ========================================

export interface AdminPendingActionsVM {
  hasActions: boolean;
  fundingRequestsToReview: {
    pendingCount: number;
  };
  fundingRequestsToSubmitToFunder: {
    pendingCount: number;
  };
  teacherPayoutsToPay: {
    pendingCount: number;
  };
  standardTeacherPayoutsToPay: {
    pendingCount: number;
  };
  studentRefundsToProcess: {
    pendingCount: number;
  };
  providerBatchInvoicesToProcess: {
    pendingCount: number;
  };
  teacherBatchInvoicesToProcess: {
    pendingCount: number;
  };
  collaborationRequestsToReview: {
    pendingCount: number;
  };
  /** Candidatures awaiting our contact only: an invitation already sent waits on the candidate, not on us. */
  batchEnrollmentCandidatesToReview: {
    pendingCount: number;
  };
  trainingInvoicesToProcess: {
    pendingCount: number;
  };
  externalProviderRequestsToReview: {
    pendingCount: number;
  };
  externalProviderPaymentsToApprove: {
    pendingCount: number;
  };
}

// ========================================
// External Provider Batch Request Types (Read)
// ========================================

export interface ExternalProviderBatchRequestVM {
  mergedStatus: EXTERNAL_PROVIDER_BATCH_ADMIN_MERGED_STATUS;

  provider: {
    name: string;
    contact: {
      firstName: string;
      lastName: string;
    };
    legalRepresentative: Nullable<{
      firstName: string;
      lastName: string;
    }>;
    email: string;
    phone: Nullable<string>;
    siret: Nullable<string>;
    country: Nullable<SUPPORTED_COUNTRY>;
    activityRegistrationNumber: Nullable<string>;
  };

  student: {
    id: string;
    firstName: string;
    lastName: string;
    balance: number;
    balanceByOrganization: Record<ORGANIZATION, number>;
  };

  request: {
    id: string;
    creationDate: string;
    approvalDate: Nullable<string>;
    rejectionDate: Nullable<string>;
    adminNote: Nullable<string>;
    studentNote: Nullable<string>;
    organization: Nullable<ORGANIZATION>;
    accessToken: string;
    status: EXTERNAL_PROVIDER_BATCH_REQUEST_STATUS;
    providerVerificationDate: Nullable<string>;
    subcontractingContract: Nullable<SubcontractingContract>;
  };

  training: {
    title: string;
    description: string;
    objective: string;
    price: number;
    hourlyPrice: number;
    trainingSchedules: ApiTrainingSchedule[];
    attachments: { url: string; label: ATTACHMENT_LABEL }[];
    websiteUrl: Nullable<string>;
    trainingLocation: Nullable<TrainingLocation>;
    equipmentDescription: Nullable<string>;
    speakers: Nullable<{ firstName: string; lastName: string; resumeUrl: Nullable<string> }[]>;
  };

  participation: Nullable<{
    id: string;
    status: EXTERNAL_PROVIDER_BATCH_PARTICIPATION_STATUS;
    creationDate: string;
    organization: ORGANIZATION;
  }>;

  /** What Opera Off already wired to the provider by hand, before the payment's approval. Definitive once recorded. */
  deposit: Nullable<{
    amount: number;
    transferDate: string;
    transactionId: string;
    externalUrl: string;
  }>;

  payment: Nullable<{
    id: string;
    estimatedPrice: number;
    amount: number;
    /** Everything Opera Off pays, deposit included. The balance transferred is this minus the deposit. */
    totalPaid: Nullable<number>;
    status: EXTERNAL_PROVIDER_BATCH_PARTICIPATION_PAYMENT_STATUS;
    invoiceNumber: string;
    invoiceUrl: string;
    invoiceDate: string;
    invoiceDeadline: string;
    creationDate: string;
    paymentDate: Nullable<string>;
    rejectionDate: Nullable<string>;
    paymentReference: Nullable<string>;
    adminNote: Nullable<string>;
    providerIban: string;
    receivingOrganization: ORGANIZATION;
    payingOrganization: ORGANIZATION;
    invoiceExternalReferenceId: Nullable<string>;
    externalUrl: Nullable<string>;
    failureReason: Nullable<EXTERNAL_PROVIDER_BATCH_PARTICIPATION_PAYMENT_FAILURE_REASON>;
  }>;
}

// ========================================
// External Provider Batch Request Write Types
// ========================================

export interface EditAndApproveExternalProviderBatchRequestBody {
  requestId: string;
  organization: ORGANIZATION;
  // Provider fields
  providerName: string;
  providerContactFirstName: string;
  providerContactLastName: string;
  providerEmail: string;
  providerPhone: string;
  providerSiret: string;
  providerCountry: SUPPORTED_COUNTRY;
  providerActivityRegistrationNumber: Nullable<string>;
  legalRepresentativeFirstName: string;
  legalRepresentativeLastName: string;
  // Training fields
  title: string;
  description: string;
  objective: string;
  price: number;
  trainingSchedules: ApiTrainingSchedule[];
  attachments: { url: string; label: ATTACHMENT_LABEL }[];
  websiteUrl: Nullable<string>;
  trainingLocation: AddressLookup;
  trainingAddressComplement: Nullable<string>;
  equipmentDescription: string;
  speakers: Speaker[];
  // Notes
  studentNote: Nullable<string>;
  adminNote: Nullable<string>;
}

export interface RejectExternalProviderBatchRequestBody {
  requestId: string;
  adminNote: string;
}

// ========================================
// External Provider Payment Write Types
// ========================================

/** Corrected invoice substituted for the one the provider submitted, carried by the approval. */
export interface ExternalProviderCorrectedInvoice {
  amount: number;
  invoiceNumber: string;
  invoiceUrl: string;
  invoiceDate: string;
  invoiceDeadline: string;
}

export interface ApproveExternalProviderPaymentBody {
  paymentId: string;
  /** Everything Opera Off pays, deposit included. */
  totalPaid: number;
  adminNote: Nullable<string>;
  paymentDate: string;
  paymentReference: string;
  /** Sent only when the admin corrected the invoice before approving; `null` approves the one submitted, untouched. */
  correctedInvoice: Nullable<ExternalProviderCorrectedInvoice>;
}

export interface ExternalProviderDepositPreviewVM {
  transactionId: string;
  /** Signed as on the bank statement: a debit is negative. */
  amount: number;
  date: string;
  label: Nullable<string>;
}

export interface ExternalProviderDepositTransactionRef {
  participationId: string;
  transactionId: string;
}

export interface RefuseExternalProviderPaymentBody {
  paymentId: string;
  adminNote: Nullable<string>;
}

// ========================================
// Batch Participation Payment Types
// ========================================

export interface ApproveBatchParticipationPaymentBody {
  paymentId: string;
  amountPaid: number;
  adminNote: Nullable<string>;
  paymentDate: string;
  paymentReference: string;
}

export interface RefuseBatchParticipationPaymentBody {
  paymentId: string;
  adminNote: string;
}

// ========================================
// Lesson Types
// ========================================

export type LessonPaginationParams = PaginationParams<"studentLastName" | "teacherLastName" | "organization" | "creationDate" | "date">;

export enum STUDENT_SIGNATURE_STATUS {
  SIGNED = "signed",
  UNSIGNED = "unsigned",
  NOT_AVAILABLE = "not_available",
}

export interface AdminLessonListVM {
  id: string;
  creationDate: string;
  date: string;
  student: {
    id: string;
    name: string;
    lastName: string;
  };
  teacher: { id: string; name: string; lastName: string };
  hours: number;
  hourlyPrice: number;
  location: LessonLocationVM;
  amountChargedOnStudentBalance: number;
  paidToTeacher: number;
  privateNote: Nullable<string>;
  organization: ORGANIZATION;
  status: "paid" | "in_payout" | "to_pay" | "cancelled";
  studentSignatureStatus: STUDENT_SIGNATURE_STATUS;
}

// ========================================
// Student Management Types
// ========================================

export interface StudentSearchItem {
  studentId: string;
  name: string;
  lastName: string;
  userStatus: USER_STATUS;
  balance: number;
  afdasISWaitingPeriodEndDate: Nullable<string>;
  isFundingRequestInProgress: boolean;
}

export enum SEARCH_ENTITY_TYPE {
  STUDENT = "student",
  TEACHER = "teacher",
  PROVIDER = "provider",
}

interface StudentSearchResultMetadata {
  userStatus: USER_STATUS;
  balance: number;
  afdasISWaitingPeriodEndDate: Nullable<Date>;
  isFundingRequestInProgress: boolean;
}

export interface StudentSearchResult {
  entityType: SEARCH_ENTITY_TYPE.STUDENT;
  entityId: string;
  label: string;
  metadata: StudentSearchResultMetadata;
}

interface TeacherSearchResultMetadata {
  standing: TEACHER_STANDING;
  organization: ORGANIZATION;
}

export interface TeacherSearchResult {
  entityType: SEARCH_ENTITY_TYPE.TEACHER;
  entityId: string;
  label: string;
  metadata: TeacherSearchResultMetadata;
}

interface ProviderSearchResultMetadata {
  tag: string;
}

export interface ProviderSearchResult {
  entityType: SEARCH_ENTITY_TYPE.PROVIDER;
  entityId: string;
  label: string;
  metadata: ProviderSearchResultMetadata;
}

export interface AdminGlobalSearchVM {
  [SEARCH_ENTITY_TYPE.STUDENT]: StudentSearchResult[];
  [SEARCH_ENTITY_TYPE.TEACHER]: TeacherSearchResult[];
  [SEARCH_ENTITY_TYPE.PROVIDER]: ProviderSearchResult[];
}

// ========================================
// Internal Training Types (moved from internal-training.ts)
// ========================================

export interface InternalTrainingOrderedQuestion {
  text: string;
  order: number;
}

export interface InternalTrainingVM {
  id: string;
  creationDate: string;
  tag: string;
  title: string;
  objective: string;
  description: string;
  team: string;
  price: number;
  questions: InternalTrainingOrderedQuestion[];
  isAdminOnly: boolean;
}

export interface AdminCreateInternalTrainingBody {
  title: string;
  tag: string;
  objective: string;
  description: string;
  team: string;
  price: number;
  questions: InternalTrainingOrderedQuestion[];
  isAdminOnly: boolean;
}

export type AdminUpdateInternalTrainingBody = AdminCreateInternalTrainingBody & { trainingId: string };

// ========================================
// Refund Types
// ========================================

export interface RefundVM {
  id: string;
  creationDate: string;
  student: string;
  totalAmount: number;
  amountPaid: Nullable<number>;
  status: PaymentStatus;
  invoices: { amount: number; url: string }[];
  iban: string;
  paymentLabel: string;
  paymentDate: Nullable<string>;
  note: Nullable<string>;
  adminNote: Nullable<string>;
  receivingOrganization: Nullable<ORGANIZATION>;
  payingOrganization: Nullable<ORGANIZATION>;
}

export interface UpdateRefundBody {
  adminNote: Nullable<string>;
  paymentDate: Nullable<string>;
  status: PaymentStatus;
  amountPaid: Nullable<number>;
  receivingOrganization: Nullable<ORGANIZATION>;
  payingOrganization: Nullable<ORGANIZATION>;
}

// ========================================
// Balance Adjustment Types (moved from balance-adjustment.ts)
// ========================================

export interface AdminBalanceAdjustmentListVM {
  id: string;
  creationDate: string;
  student: string;
  amount: number;
  label: string;
  note: Nullable<string>;
  documentUrl: Nullable<string>;
  receivingOrganization: ORGANIZATION;
  payingOrganization: ORGANIZATION;
}

export interface CreateBalanceAdjustmentBody {
  studentId: string;
  label: string;
  amount: number;
  note: Nullable<string>;
  documentUrl: Nullable<string>;
  receivingOrganization: ORGANIZATION;
  payingOrganization: ORGANIZATION;
}

// ========================================
// Training Follow-Up Pagination Types (moved from retrieve-training-follow-ups.ts)
// ========================================

export type TrainingFollowUpSortField =
  | "status"
  | "creationDate"
  | "trainingStartDate"
  | "trainingEndDate"
  | "studentLastName"
  | "adminLastName"
  | "organization"
  | "hoursPlanned"
  | "hoursTaken";

export interface TrainingFollowUpPaginationParams extends Omit<PaginationParams<TrainingFollowUpSortField>, "filters"> {
  filters?: {
    searchTerm?: string;
    organization?: ORGANIZATION;
    includedStatuses?: TRAINING_FOLLOW_UP_STATUS[];
    adminIds?: string[];
    minHoursCompletionRatio?: number;
  };
}

// ========================================
// Training Follow-Up Types
// ========================================

export interface TrainingFollowUpAttendanceSheetVM {
  id: string;
  studentSignatureUrl: string | null;
  startTime: string;
  endTime: string;
  backfilledForInterval: Nullable<DateInterval>;
  context: {
    educationalAdvisor: {
      id: string;
      name: string;
      type: EDUCATIONAL_ADVISOR;
    };
    source: {
      type: TRAINING_ITEM_TYPE;
      label: string;
    };
  };
  number: number;
}

export interface TrainingFollowUpSurveyVM {
  question: {
    id: string;
    text: string;
    type: SURVEY_QUESTION_TYPE;
    options: { min: number; max: number } | null;
  };
  response: string | number | null;
}

export interface TrainingFollowUpTrainingItemObjectiveVM {
  id: string;
  quantifiableObjective: string;
  acquisitionStatus: OBJECTIVE_ACQUISITION_STATUS | null;
  number: number;
  assignmentDate: string | null;
  assignedEducationalAdvisor: Nullable<{ id: string; name: string; type: EDUCATIONAL_ADVISOR }>;
  isAbandoned: boolean;
  isCustom: boolean;
}

/** L'appréciation d'un conseiller sur l'élément de formation. L'admin voit aussi la privée. */
export interface TrainingFollowUpTrainingItemAppraisalVM {
  id: string;
  educationalAdvisor: { id: string; type: EDUCATIONAL_ADVISOR; name: Nullable<string> };
  disclosedAppraisal: string;
  privateAppraisal: Nullable<string>;
  submittedAt: string;
}

export interface TrainingFollowUpTrainingItemVM {
  type: TRAINING_ITEM_TYPE;
  details: {
    title: string;
    description: string;
  };
  educationalAdvisors: {
    id: string;
    userName: string;
  }[];
  objectives: TrainingFollowUpTrainingItemObjectiveVM[];
  // Une par conseiller ayant rendu son évaluation sur cet élément de formation.
  appraisals: TrainingFollowUpTrainingItemAppraisalVM[];
  survey: TrainingFollowUpSurveyVM[];
}

export interface TrainingFollowUpWithUrgenciesVM {
  id: string;
  interval: DateInterval;
  title: string;
  availableActionUrgency: {
    student: {
      attendanceSheet: ACTION_URGENCY_LEVEL;
      objectiveAssignment: ACTION_URGENCY_LEVEL;
    };
    educationalAdvisor: {
      objectiveCompletion: ACTION_URGENCY_LEVEL;
    };
  };
  student: {
    id: string;
    name: string;
    lastName: string;
    admin: {
      id: string;
      name: string;
      lastName: string;
    };
    expensesAmountSinceFundingObtention: number;
  };
  hasAfdasExpenses: boolean;
  advanceEndDateToHourQuotaTarget: Nullable<string>;
  organization: ORGANIZATION;
  status: TRAINING_FOLLOW_UP_STATUS;
  progress: {
    hoursPlanned: number;
    hoursTaken: number;
    hoursScheduled: number;
    // Le statut d'allure servi par le backend, `null` pour une formation annulée ou terminée sans être hors délai.
    paceStatus: Nullable<TRAINING_PACE_STATUS>;
  };
}

export interface AdminTrainingFollowUpVM {
  id: string;
  fundingRequestId: string;
  externalFundingRequestId: string;
  student: {
    id: string;
    name: string;
    trainingInvitationUrl: string;
    attendanceCertificateUrl: Nullable<string>;
    completionCertificateUrl: Nullable<string>;
    adminName: string;
  };
  training: {
    interval: DateInterval;
    attendanceSheetCollectionInterval: DateInterval;
    title: string;
    status: TRAINING_FOLLOW_UP_STATUS;
  };
  hasAfdasExpenses: boolean;
  availableActionUrgency: {
    student: {
      attendanceSheet: ACTION_URGENCY_LEVEL;
      objectiveAssignment: ACTION_URGENCY_LEVEL;
    };
    educationalAdvisor: {
      objectiveCompletion: ACTION_URGENCY_LEVEL;
    };
  };
  progress: {
    hoursPlanned: number;
    hoursTaken: number;
    hoursScheduled: number;
    // Le statut d'allure servi par le backend, `null` pour une formation annulée ou terminée sans être hors délai.
    paceStatus: Nullable<TRAINING_PACE_STATUS>;
  };
}

// ========================================
// Training Invoice Types
// ========================================

export enum TrainingInvoiceStatus {
  TO_SEND_TO_PAYMENT_PROVIDER = "to_send_to_payment_provider",
  CANCELLED = "cancelled",
  FAILED_TO_SEND = "failed_to_send",
  PAID = "paid",
  UPCOMING = "upcoming",
  LATE = "late",
  DRAFT = "draft",
  // The training was extended after this invoice was already sent to the payment provider: the invoice is now stale
  // (wrong training dates) and a manual credit note (avoir) is required before the corrected invoice can be reissued.
  CREDIT_NOTE_REQUIRED = "credit_note_required",
  // The credit note (avoir) has been issued for a stale invoice.
  CREDIT_NOTE_ISSUED = "credit_note_issued",
}

export interface TrainingInvoice {
  id: string;
  student: {
    id: string;
    name: string;
    lastName: string;
    expensesAmountSinceFundingObtention: number;
  };
  fundingRequest: {
    funder: FUNDER;
    externalFundingRequestId: string;
    wantsAfdasExpenses: boolean;
  };
  training: {
    interval: DateInterval;
    hours: number;
  };
  // Live hour consumption of the underlying training, as displayed on the "Suivi qualité" list. hoursPlanned is the
  // training's planned hours, set once when the follow-up starts and never mutated afterwards (an end-date change
  // moves the interval only), so it is also what the invoice billed.
  progress: {
    hoursPlanned: number;
    hoursTaken: number;
    hoursScheduled: number;
  };
  invoice: {
    creationDate: string;
    amount: number;
    status: TrainingInvoiceStatus;
    number: string | null;
    externalUrl: string | null;
  };
}

export type TrainingInvoiceSortField = "creationDate" | "amount" | "status";
export type TrainingInvoiceFilterableStatus =
  | "to_send_to_payment_provider"
  | "sent_to_payment_provider"
  | "failed_to_send"
  | "cancelled"
  | "credit_note_required";
export interface TrainingInvoiceListPaginationParams extends Omit<PaginationParams<TrainingInvoiceSortField>, "filters"> {
  filters?: {
    searchTerm?: string;
    statuses?: TrainingInvoiceFilterableStatus[];
  };
}

// ========================================
// Timeline Event Types (moved from timeline-event.ts)
// ========================================

export enum TIMELINE_EVENT_TITLE {
  PHONE_CALL = "Appel téléphonique",
  EMAIL_EXCHANGE = "Echange d'email",
  SMS = "SMS",
}

export enum TIMELINE_EVENT_TYPE {
  ADMIN_NOTE = "admin_note",
  ISSUE_REPORT = "issue_report",
}

export type TIMELINE_EVENT_DETAILS =
  | { type: TIMELINE_EVENT_TYPE.ADMIN_NOTE; payload: { description: string } }
  | { type: TIMELINE_EVENT_TYPE.ISSUE_REPORT; payload: { type: ISSUE_TYPE; description: string; sourceRole: ROLES } };

export type TimelineEventAuthorRole = ROLES | "admin";

export type TimelineEventVM = {
  id: string;
  targetedUserId: string;
  title: string;
  eventDate: string;
  creationDate: string;
  author: {
    role: TimelineEventAuthorRole;
    accountId: string;
    name: string;
  };
  details: TIMELINE_EVENT_DETAILS;
};

export type AddTimelineEventBody = {
  title: string;
  eventDate: string;
  description: string;
  targetedUserId: string;
};

export type UpdateTimelineEventBody = {
  timelineEventId: string;
  title: string;
  eventDate: string;
  description: string;
};

// ========================================
// Admin Repository Interface
// ========================================

export interface AdminRepository {
  // Pending Actions
  hasPendingActions(): Promise<AdminPendingActionsVM>;

  // External Provider Batch Requests
  listExternalProviderBatchRequests(): Promise<ExternalProviderBatchRequestVM[]>;
  getExternalProviderBatchRequest(requestId: string): Promise<ExternalProviderBatchRequestVM>;
  editAndApproveExternalProviderBatchRequest(body: EditAndApproveExternalProviderBatchRequestBody): Promise<void>;
  rejectExternalProviderBatchRequest(body: RejectExternalProviderBatchRequestBody): Promise<void>;

  // External Provider Payments
  approveExternalProviderPayment(body: ApproveExternalProviderPaymentBody): Promise<void>;
  previewExternalProviderDeposit(body: ExternalProviderDepositTransactionRef): Promise<ExternalProviderDepositPreviewVM>;
  recordExternalProviderDeposit(body: ExternalProviderDepositTransactionRef): Promise<void>;
  refuseExternalProviderPayment(body: RefuseExternalProviderPaymentBody): Promise<void>;

  // Batches
  listAllBatches(organizerType?: BATCH_ORGANIZER_TYPE): Promise<AdminBatchListItem[]>;
  getBatchDetail(id: string): Promise<AdminBatchDetailVM>;
  checkBatchParticipationCompletion(batchId: string): Promise<void>;

  // Batch Enrollment Invitations
  listBatchEnrollmentCandidates(): Promise<BatchEnrollmentCandidateVM[]>;
  approveBatchEnrollmentInvitation(invitationId: string): Promise<void>;
  rejectBatchEnrollmentInvitation(invitationId: string, rejectionReason: string): Promise<void>;

  // Batch Payments
  listBatchPayments(organizerType: BATCH_ORGANIZER_TYPE): Promise<BatchPaymentVM[]>;

  // Batch Participation Payments
  approvePayment(body: ApproveBatchParticipationPaymentBody): Promise<void>;
  refusePayment(body: RefuseBatchParticipationPaymentBody): Promise<void>;
  setEnrollmentBalanceBypass(enrollmentId: string, enabled: boolean): Promise<void>;

  // Studios
  listStudios(): Promise<AdminStudioListItem[]>;
  getStudioById(id: string): Promise<AdminStudioListItem>;
  createStudio(body: CreateAdminStudioBody): Promise<string>;
  updateStudio(body: UpdateAdminStudioBody): Promise<void>;

  // User Management
  impersonate(targetUserId: string, activeRole: ROLES): Promise<ImpersonateResponseVM>;
  retrieveUnsubscribedUserList(): Promise<UserVM[]>;
  retrieveUserList(): Promise<UserVM[]>;
  getImpersonationCandidates(role: ROLES): Promise<ImpersonationCandidateVM[]>;
  getUser(userId: string): Promise<UserDetailVM>;
  adminList(): Promise<AdminVM[]>;

  // Funding Requests
  adminFundingRequestEligibility(studentId: string): Promise<FundingRequestEligibilityVM>;
  getAll(filters?: {
    status: FUNDING_REQUEST_STATUS[];
    funders?: string[];
    trainingItemTypes?: TRAINING_ITEM_TYPE[];
  }): Promise<FundingRequestListItemVM>;
  adminGetFundingList(params: FundingListPaginationParams): Promise<PaginatedResult<FundingListItemVM>>;
  review(fundingRequestId: string, fundingRequest: ReviewFundingRequestBody): Promise<void>;
  get(fundingRequestId: string): Promise<FundingRequestVM>;
  getApproved(fundingRequestId: string): Promise<ApprovedFundingRequestDetailVM>;
  delete(fundingRequestId: string): Promise<void>;
  submitToFunder(fundingRequestId: string, body: { hasBeenSubmitted: boolean }): Promise<void>;
  updateErrorReason(
    fundingRequestId: string,
    body: { error: FUNDING_REQUEST_ERROR_REASONS; sendNotificationToStudent: boolean },
  ): Promise<void>;
  revertFundingRequestToReview(fundingRequestId: string): Promise<void>;
  updateFundingRequest(fundingRequest: UpdateFundingRequestBody): Promise<void>;
  approve(fundingRequest: ApproveFundingRequestBody): Promise<void>;
  cancelFunding(fundingRequestId: string): Promise<void>;

  getEligibleTrainingItems(studentId: string): Promise<EligibleTrainingItemsVM>;

  // Payouts
  getAdminTeacherPayouts(params: TeacherPayoutListPaginationParams): Promise<PaginatedResult<AdminTeacherPayoutVM>>;
  validateTeacherPayout(command: ValidateTeacherPayoutBody): Promise<void>;
  replaceTeacherPayoutInvoice(payoutId: string, invoiceUrl: string): Promise<void>;
  replaceProviderPayoutInvoice(payoutId: string, invoiceUrl: string): Promise<void>;

  // Lessons
  listLessons(params: LessonPaginationParams): Promise<PaginatedResult<AdminLessonListVM>>;
  teacherLessons(teacherId: string): Promise<TeacherLessonListVM[]>;

  // Unified User Creation
  createUser(body: CreateUserBody): Promise<string>;

  // Update roles (assign new + edit existing) for a user
  updateRoles(userId: string, body: UpdateRolesBody): Promise<void>;

  // User Account (shared across student/teacher/provider detail pages)
  updateUserAccount(userId: string, body: UpdateUserAccountBody): Promise<void>;

  // Student Management
  getStudent(id: string): Promise<AdminStudentVM>;
  listStudents(): Promise<StudentListElementVM[]>;
  globalSearch(query: string): Promise<AdminGlobalSearchVM>;
  updateStudentSettings(studentId: string, body: UpdateStudentSettingsBody): Promise<void>;
  getStudentSummary(id: string): Promise<StudentSummaryVM>;

  // Teacher Management
  getTeacher(id: string): Promise<AdminTeacherVM>;
  listTeachers(): Promise<TeacherListElementVM[]>;
  updateTeacherSettings(teacherId: string, body: UpdateTeacherSettingsBody): Promise<void>;

  // Provider Management
  listProviders(): Promise<ProviderListItemVM[]>;
  getProvider(id: string): Promise<Nullable<AdminProviderVM>>;
  updateProviderSettings(providerId: string, body: UpdateProviderSettingsBody): Promise<void>;

  // Internal Training
  listInternalTrainings(): Promise<InternalTrainingVM[]>;
  createInternalTraining(training: AdminCreateInternalTrainingBody): Promise<string>;
  updateInternalTraining(training: AdminUpdateInternalTrainingBody): Promise<void>;
  deleteInternalTraining(trainingId: string): Promise<void>;

  // Documentary Resources (catalog management)
  getDocumentaryResourcesCatalog(): Promise<AdminDocumentaryResourceVM[]>;
  createDocumentaryResource(body: CreateDocumentaryResourceBody): Promise<string>;
  updateDocumentaryResource(body: UpdateDocumentaryResourceBody): Promise<void>;
  archiveDocumentaryResource(resourceId: string): Promise<void>;

  // Refunds
  listRefunds(): Promise<RefundVM[]>;
  updateRefund(id: string, refund: UpdateRefundBody): Promise<void>;

  // Balance Adjustments
  listBalanceAdjustments(): Promise<AdminBalanceAdjustmentListVM[]>;
  createBalanceAdjustment(balanceAdjustment: CreateBalanceAdjustmentBody): Promise<string>;

  // Training Follow-Ups
  adminGetAll(params: TrainingFollowUpPaginationParams): Promise<PaginatedResult<TrainingFollowUpWithUrgenciesVM>>;
  adminGetOne(id: string): Promise<AdminTrainingFollowUpVM>;
  changeTrainingFollowUpEndDate(payload: { trainingFollowUpId: string; endDate: Date }): Promise<void>;
  advanceTrainingFollowUpEndDateToHourQuota(trainingFollowUpId: string): Promise<void>;
  getAttendanceSheets(trainingFollowUpId: string): Promise<TrainingFollowUpAttendanceSheetVM[]>;
  getTrainingItems(trainingFollowUpId: string): Promise<TrainingFollowUpTrainingItemVM[]>;
  backfillAttendanceSheets(trainingId: string): Promise<void>;

  // Training Invoices
  listTrainingInvoices(params: TrainingInvoiceListPaginationParams): Promise<PaginatedResult<TrainingInvoice>>;
  createCustomerInvoice(trainingInvoiceId: string): Promise<void>;

  // Transfers
  listTransfers(): Promise<StandardPayment[]>;
  listConventionTransfers(): Promise<ConventionPaymentVM[]>;
  updateTransfer(id: string, transfer: UpdatePaymentBody): Promise<string>;
  listTransferLessons(id: string): Promise<LessonListVM>;

  // Timeline Events
  listTimelineEvents(targetedUserId: string): Promise<TimelineEventVM[]>;
  createTimelineEvent(event: AddTimelineEventBody): Promise<void>;
  updateTimelineEvent(event: UpdateTimelineEventBody): Promise<void>;

  // User Invitations
  refreshUserInvitation(userId: string): Promise<void>;
  listUserInvitations(): Promise<UserInvitationVM[]>;
  listUninvitedUsers(): Promise<UninvitedUserVM[]>;
  sendUserInvitation(userId: string): Promise<void>;
}
